// Central content source for the Case Studies page.
// One typed data model, ready for the website administration area.
// Presentation logic lives in components — never hard-code case studies in JSX.
import { getLive } from '@/lib/liveContent';

export const CASE_STUDIES_SEO = {
  title: 'Business Consultancy Case Studies | Amden Group',
  description:
    'Explore how Amden Group helps businesses improve operations, strengthen people, grow their market presence and build stronger business foundations.',
};

export const CASE_STUDY_CATEGORIES = {
  operations: { label: 'Operations', color: '#FFB800', soft: '#FFF3CC', dark: '#9C7400' },
  people: { label: 'People', color: '#D6318B', soft: '#FBDCEE', dark: '#A01E66' },
  growth: { label: 'Growth', color: '#029E88', soft: '#CCF2EC', dark: '#01755F' },
  launch: { label: 'Launch', color: '#4225C4', soft: '#E4E0FA', dark: '#331BA0' },
  'multi-service': { label: 'Multi-Service', color: '#0B1729', soft: '#E2E8F2', dark: '#0B1729' },
};

/**
 * @typedef {Object} CaseStudy
 * CMS-ready model. Not all fields are required for the listing card.
 * Do not publish drafts or unverified metrics.
 *
 * @property {string} id
 * @property {string} slug                 — stable route: /case-studies/[slug]
 * @property {string} client
 * @property {string} title
 * @property {string} summary              — one-sentence challenge summary
 * @property {string|null} challenge       — full verified challenge text
 * @property {string|null} solution        — full verified solution text
 * @property {string[]} outcomes           — verified outcomes only
 * @property {{label: string, value: string}[]} metrics — verified figures only
 * @property {{quote: string, name: string, position: string|null, company: string}|null} testimonial
 * @property {string|null} industry        — only when verified
 * @property {string[]} categories         — keys of CASE_STUDY_CATEGORIES (multi allowed)
 * @property {string[]} services
 * @property {{src: string, alt: string}|null} image
 * @property {{src: string, alt: string}[]} gallery
 * @property {{src: string, alt: string}|null} logo
 * @property {'draft'|'published'} status
 * @property {boolean} featured
 * @property {number} order
 * @property {{title: string, description: string}} seo
 * @property {string[]} related            — slugs of related case studies
 */
export const CASE_STUDIES = [
  {
    id: 'cs-greenville-idc',
    slug: 'greenville-idc',
    client: 'Greenville IDC',
    title: 'Greenville IDC Multi-Region Growth Strategy',
    summary:
      'A multi-region marketing and growth strategy bringing clarity and consistency across Ireland, Northern Ireland, Scotland and England.',
    challenge:
      'Greenville IDC required clearer strategic direction, stronger marketing consistency, and improved operational alignment across Ireland, Northern Ireland, Scotland, and England as the business continued to grow across multiple regions.',
    solution:
      'Amden Growth provided strategic marketing support, regional alignment, and ongoing consultancy designed to improve visibility, strengthen brand consistency, and create a more structured approach to growth across all four regions.',
    outcomes: [
      'Improved clarity and strategic direction',
      'Stronger branding and regional consistency',
      'Better alignment across multiple regions',
    ],
    metrics: [],
    testimonial: {
      quote:
        'Working with Amden Group brought real structure and momentum to our operations. Their team took the time to understand our business and delivered practical solutions that genuinely improved efficiency and communication across the company. Professional, responsive, and easy to work with from start to finish.',
      name: 'Simon Murray',
      position: null,
      company: 'Greenville IDC',
    },
    industry: null,
    categories: ['growth'],
    services: ['Marketing strategy', 'Regional alignment', 'Ongoing consultancy'],
    image: {
      src: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1080&q=80',
      alt: 'Industrial warehouse interior representing a multi-region operation',
    },
    gallery: [],
    logo: null,
    status: 'published',
    featured: true,
    order: 1,
    seo: {
      title: 'Greenville IDC Multi-Region Growth Strategy | Amden Group',
      description: 'How Amden Growth supported Greenville IDC with strategy, branding consistency and regional alignment.',
    },
    related: ['contech-dpd'],
  },
  {
    id: 'cs-contech-dpd',
    slug: 'contech-dpd',
    client: 'Contech DPD',
    title: 'Contech DPD Business Launch',
    summary:
      'A coordinated business launch — professional brand, new website and business email systems delivered together.',
    challenge:
      'Contech DPD was preparing to launch and needed a professional brand, website and core business systems in place before trading — without juggling multiple disconnected suppliers.',
    solution:
      'Amden Launch provided joined-up support — creating the professional logo and brand identity, building and launching the new website, and configuring business email systems — all coordinated through one practical launch plan.',
    outcomes: [
      'Professional logo and brand identity created',
      'New website launched and fully supported',
      'Business email systems professionally configured',
    ],
    metrics: [],
    testimonial: null,
    industry: null,
    categories: ['launch'],
    services: ['Brand identity', 'Website development', 'Email systems'],
    image: {
      src: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1080&q=80',
      alt: 'Construction professionals reviewing plans on site',
    },
    gallery: [],
    logo: null,
    status: 'published',
    featured: false,
    order: 2,
    seo: {
      title: 'Contech DPD Business Launch | Amden Group',
      description: 'How Amden Launch supported Contech DPD with brand identity, a new website and business systems.',
    },
    related: ['greenville-idc'],
  },
  {
    id: 'cs-kemmlit-ireland',
    slug: 'kemmlit-ireland',
    client: 'Kemmlit Ireland',
    title: 'Kemmlit Ireland – HR & Recruitment Support',
    summary:
      'Ongoing, practical HR and recruitment support embedded with a growing business.',
    challenge:
      'Kemmlit Ireland required stronger recruitment structure, clearer onboarding processes, and practical HR support as the business continued to grow. Internal management teams were spending increasing amounts of time managing recruitment, onboarding, and employee processes.',
    solution:
      'Amden People provided ongoing HR advice, recruitment support, and onboarding improvements designed to strengthen internal structure, improve hiring consistency, and support long-term workforce growth across the business.',
    outcomes: [
      'Improved recruitment structure and visibility',
      'Stronger onboarding process',
      'Reduced pressure on internal management',
    ],
    metrics: [],
    testimonial: null,
    industry: null,
    categories: ['people'],
    services: ['HR support', 'Recruitment support', 'Onboarding improvements'],
    image: {
      src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1080&q=80',
      alt: 'Contemporary office interior representing a structured workplace',
    },
    gallery: [],
    logo: null,
    status: 'published',
    featured: false,
    order: 3,
    seo: {
      title: 'Kemmlit Ireland – HR & Recruitment Support | Amden Group',
      description: 'How Amden People supports Kemmlit Ireland with HR, recruitment structure and onboarding.',
    },
    related: ['outlook-gardens'],
  },
  {
    id: 'cs-outlook-gardens',
    slug: 'outlook-gardens',
    client: 'Outlook Gardens',
    title: 'Outlook Gardens – Retainer Recruitment',
    summary: 'Structured retainer recruitment providing ongoing, reliable hiring support.',
    challenge: null,
    solution: null,
    outcomes: [],
    metrics: [],
    testimonial: null,
    industry: null,
    categories: ['people'],
    services: ['Retainer recruitment'],
    image: {
      src: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1080&q=80',
      alt: 'Modern garden room exterior in a landscaped garden',
    },
    gallery: [],
    logo: null,
    status: 'published',
    featured: false,
    order: 4,
    seo: {
      title: 'Outlook Gardens – Retainer Recruitment | Amden Group',
      description: 'Retainer recruitment support for Outlook Gardens from Amden People.',
    },
    related: ['kemmlit-ireland'],
  },
  {
    id: 'cs-robotic-lawn-mower-experts',
    slug: 'robotic-lawn-mower-experts',
    client: 'Robotic Lawn Mower Experts',
    title: 'Robotic Lawn Mower Experts – Digital Growth & Lead Generation',
    summary: 'Digital growth and lead-generation support focused on consistent, measurable enquiries.',
    challenge: null,
    solution: null,
    outcomes: [],
    metrics: [],
    testimonial: null,
    industry: null,
    categories: ['growth'],
    services: ['Digital growth', 'Lead generation'],
    image: {
      src: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=1080&q=80',
      alt: 'Freshly mowed striped lawn',
    },
    gallery: [],
    logo: null,
    status: 'published',
    featured: false,
    order: 5,
    seo: {
      title: 'Robotic Lawn Mower Experts – Digital Growth | Amden Group',
      description: 'Digital growth and lead-generation support from Amden Growth.',
    },
    related: ['greenville-idc'],
  },
  {
    id: 'cs-rmg-fitness',
    slug: 'rmg-fitness',
    client: 'RMG Fitness',
    title: 'RMG Fitness',
    summary: 'Practical business support for a growing fitness business.',
    challenge: null,
    solution: null,
    outcomes: [],
    metrics: [],
    testimonial: null,
    industry: null,
    categories: ['operations'],
    services: [],
    image: {
      src: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1080&q=80',
      alt: 'Modern gym interior with training equipment',
    },
    gallery: [],
    logo: null,
    status: 'published',
    featured: false,
    order: 6,
    seo: {
      title: 'RMG Fitness | Amden Group',
      description: 'Business support for RMG Fitness from Amden Group.',
    },
    related: [],
  },
];

// Centrally configurable featured case study (used when none is flagged in data).
export const FEATURED_CASE_STUDY_ID = 'cs-greenville-idc';

// Live CMS getter — published case studies from the database are the source
// of truth after migration; CASE_STUDIES remains the logged fallback.
export const getCaseStudies = () => getLive('caseStudies') || CASE_STUDIES;

export const CASE_STUDIES_HERO = {
  eyebrow: 'Case Studies',
  headingA: 'Real Results from',
  headingAccent: 'Real Businesses',
  body: 'See how Amden works alongside businesses to solve practical challenges, improve performance and build stronger foundations for growth.',
  supportingLine: 'Practical support across operations, people, growth and business launch.',
  primaryCta: { label: 'Explore Client Results', target: '#case-study-results' },
  secondaryCta: { label: 'Discuss Your Challenge', href: '/?source=Case%20Studies#solution-finder' },
  image: {
    src: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1200&q=80',
    alt: 'Consultant leading a business review workshop with a client team',
    width: 1200,
    height: 900,
  },
  statCard: { label: 'Client Projects Delivered', value: '150+' },
};

export const RESULT_TYPES = {
  eyebrow: 'Practical Outcomes',
  heading: 'Results that strengthen the business',
  intro:
    'Every engagement is different, but our work is focused on practical improvements that can be implemented, monitored and built upon.',
  items: [
    { icon: 'eye', title: 'Greater Operational Visibility', text: 'Clearer reporting, priorities and management information.', color: '#FFB800' },
    { icon: 'file-minus', title: 'Reduced Administration', text: 'Less duplication, paperwork and unnecessary manual processing.', color: '#D6318B' },
    { icon: 'users', title: 'Stronger People Processes', text: 'More consistent recruitment, onboarding, management and development.', color: '#029E88' },
    { icon: 'clipboard-check', title: 'Clearer Accountability', text: 'Defined ownership, deadlines and follow-through.', color: '#4225C4' },
    { icon: 'route', title: 'Better Customer Journeys', text: 'Stronger websites, messaging, enquiries and sales processes.', color: '#FFB800' },
    { icon: 'boxes', title: 'Scalable Systems', text: 'Processes and tools designed to support future growth.', color: '#D6318B' },
    { icon: 'building-2', title: 'Stronger Business Foundations', text: 'Practical structure, planning and launch support.', color: '#029E88' },
  ],
};

export const CASE_STUDIES_PROCESS = {
  eyebrow: 'How We Work',
  heading: 'From business challenge to practical improvement',
  stages: [
    {
      number: '01',
      title: 'Understand',
      text: 'We examine the business, the people involved and the underlying cause of the challenge.',
    },
    {
      number: '02',
      title: 'Plan',
      text: 'We agree clear priorities, practical actions, responsibilities and expected outcomes.',
    },
    {
      number: '03',
      title: 'Implement',
      text: 'We work alongside the team to introduce the required changes, systems or support.',
    },
    {
      number: '04',
      title: 'Improve',
      text: 'We review progress, measure performance and continue strengthening the solution.',
    },
  ],
  diagramLabel: 'Continuous improvement cycle: understand, plan, implement, then improve.',
};

export const CASE_STUDIES_CTA = {
  index: '07',
  eyebrow: 'Your Business',
  heading: 'Facing a similar challenge?',
  copy: 'Tell us what is limiting performance, creating pressure or slowing growth, and we’ll help identify the most practical way forward.',
  primaryLabel: 'Discuss Your Challenge',
  secondaryLabel: 'Explore Our Services',
  secondaryHref: '/services',
};

// Session key linking the active category filter to the enquiry journey.
export const CASE_STUDY_FILTER_KEY = 'amdenCaseStudyCategory';
